import { Params, redirect, replace } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { ROUTES } from '@constants';
import { useApplicationStore } from '@data';
import { logLead } from '@utils';
import { ProviderAvailability } from '@types';
import { DRUG_SCREEN_ROUTE_PATHNAME } from '@router/routes/routePathnames';
import {
  enrollInMatExperimentIfQualifies,
  getIntakeQuizRedirectPath,
} from '@lib/experiments/matQuestions';
import { ensureFeatureFlagsFetched } from '@lib/firebase';
import { loadServiceTypesByState, VisitTypeIds } from '../utils';
import { fetchAvailabilities } from './utils';

export type ProviderAvailabilitiesPromise = Promise<
  ProviderAvailability[] | void
>;

const MHTRedirectUrl =
  'https://quick.md/treatment/waitlist/menopause-hormone-therapy/';

export type AppointmentTimePreferenceLoader = {
  fetchAvailabilities: ProviderAvailabilitiesPromise;
};

export async function appointmentTimePreferenceLoader({
  params,
}: {
  params: Params<string>;
}): Promise<AppointmentTimePreferenceLoader | Response> {
  await ensureFeatureFlagsFetched();

  const { visitTypeId } = params;

  const {
    getVisitTypeById,
    setUtmParameters,
    stateCode,
    utmParameters,
    serviceTypes,
    setVisitTypeId,
    flags,
  } = useApplicationStore.getState();

  if (!serviceTypes.length && stateCode) {
    await loadServiceTypesByState(stateCode);
  }

  setVisitTypeId(visitTypeId || null);

  logLead();

  const visitType = getVisitTypeById(visitTypeId || '');

  if (!visitType || !visitTypeId || !stateCode) {
    setUtmParameters({ ...utmParameters, service: null });

    return redirect(`${ROUTES.services.path}${stateCode ? `?state=${stateCode}` : ''}`);
  }

  // Treatment-arm MAT patients deep-linked into the curated-slots flow
  // would otherwise skip the intake quiz. Route them to the quiz first
  // (and let it own enrollment) so the cohort sees the experiment.
  const intakeQuizPath = getIntakeQuizRedirectPath(visitTypeId);
  if (intakeQuizPath) {
    return redirect(intakeQuizPath);
  }

  // Enroll in the MAT-questions experiment if the user reaches the curated
  // appointment-time-preference flow for an addiction-treatment visit type.
  // Helper handles all gating + session-scoped dedupe.
  enrollInMatExperimentIfQualifies(visitTypeId);

  if (
    visitTypeId === VisitTypeIds.MenopauseHormoneTherapy &&
    !flags.mhtStates.states.find((value) => value === stateCode)
  ) {
    return redirect(MHTRedirectUrl);
  }

  if (visitType.isDrugScreenRequired) {
    return replace(`${DRUG_SCREEN_ROUTE_PATHNAME}?visitTypeId=${visitTypeId}`);
  }

  try {
    const fetchAvailabilitiesPromise = fetchAvailabilities({
      appointmentBufferMinutes: flags.appointmentBufferMinutes,
      stateCode,
      visitTypeId,
    });

    return { fetchAvailabilities: fetchAvailabilitiesPromise };
  } catch (error) {
    captureException(error);
    return redirect(ROUTES.services.path);
  }
}
